import { useCallback, useRef, useState } from "react";
import { api, sleep } from "@/lib/api";
import { extFor } from "@/hooks/useRecorder";

export function useChunkUploader({ onQuota }) {
  const courseId = useRef(null);
  const queue = useRef([]);
  const busy = useRef(false);
  const [segments, setSegments] = useState([]);
  const [failed, setFailedState] = useState([]);
  const failedRef = useRef([]);
  const setFailed = (fn) => { failedRef.current = fn(failedRef.current); setFailedState(failedRef.current); };

  const setSeg = (index, patch) => setSegments((s) => s.map((x) => (x.index === index ? { ...x, ...patch } : x)));

  const pump = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    while (queue.current.length) {
      const item = queue.current[0];
      setSeg(item.index, { status: "uploading" });
      try {
        const fd = new FormData();
        fd.append("audio", item.blob, `segment-${item.index}.${extFor(item.type)}`);
        fd.append("index", item.index);
        fd.append("duration", item.dur.toFixed(2));
        const { data } = await api.post(`/courses/${courseId.current}/chunks`, fd, { timeout: 120000 });
        setSeg(item.index, { status: "done", text: data.text });
        queue.current.shift();
      } catch (e) {
        const code = e?.response?.status;
        if (code === 402) {
          setSeg(item.index, { status: "error" });
          queue.current = [];
          onQuota?.(e.response.data.detail);
          break;
        }
        item.attempts += 1;
        if (item.attempts >= 4 || code === 409 || code === 404) {
          setSeg(item.index, { status: "error", detail: e?.response?.data?.detail });
          setFailed((f) => [...f, item]);
          queue.current.shift();
        } else {
          setSeg(item.index, { status: "retrying" });
          await sleep(2000 * item.attempts);
        }
      }
    }
    busy.current = false;
  }, [onQuota]);

  const enqueue = useCallback((blob, dur, index, type) => {
    queue.current.push({ blob, dur, index, type, attempts: 0 });
    setSegments((s) => [...s, { index, status: "pending", text: "" }]);
    pump();
  }, [pump]);

  const retryFailed = useCallback(() => {
    failedRef.current.forEach((item) => { item.attempts = 0; queue.current.push(item); setSeg(item.index, { status: "pending" }); });
    setFailed(() => []);
    setTimeout(pump, 0);
  }, [pump]);

  const drain = useCallback(async () => {
    await sleep(600);
    while (busy.current || queue.current.length) await sleep(300);
  }, []);

  return { courseId, segments, failed, enqueue, retryFailed, drain, pending: () => busy.current || queue.current.length > 0, failedCount: () => failedRef.current.length };
}
